const FIRM_NAMES = {
  eas: "EAS Legal",
  vals: "VALS",
  lacw: "Law and Advocacy Centre for Women",
  jbl: "Jarrod Behan Law",
  admin: "Complete Law Support",
};

const escapeHtml = (value) => String(value ?? "")
  .replace(/&/g, "&amp;")
  .replace(/</g, "&lt;")
  .replace(/>/g, "&gt;")
  .replace(/"/g, "&quot;")
  .replace(/'/g, "&#039;");

const checked = (answer, expected) => answer === expected ? "checked" : "";

export function buildTelephoneDeclarationHtml(data = {}, brandType = "lacw") {
  const clientName = data.declaration_client_name
    || data.declaration_name
    || [data.first_name, data.last_name].filter(Boolean).join(" ");
  const declarationName = data.declaration_name || clientName;
  const centrelinkName = data.centrelink_consent_name || declarationName;
  const fileReference = data.declaration_file_reference || data.file_number || data.file_no;
  const meetingDate = data.declaration_meeting_datetime || "";
  const lawyerName = data.lawyer_name || data.lacw_staff_name || "";
  const lawyerDate = data.lawyer_date || "";
  const firmName = FIRM_NAMES[brandType] || FIRM_NAMES.lacw;
  const consentMethod = data.consent_obtained_via === "Other"
    ? data.consent_obtained_via_other || "Other"
    : data.consent_obtained_via || "";

  const answerRow = (question, answer) => `
    <tr><td>${escapeHtml(question)}</td><td class="answer">
      <span class="box ${checked(answer, "Yes")}"></span> Yes
      <span class="box ${checked(answer, "No")}"></span> No
    </td></tr>`;

  return `<!doctype html><html><head><meta charset="utf-8"><title>VLA Telephone Declaration - ${escapeHtml(clientName || "Client")}</title>
  <style>
    @page { size: A4 portrait; margin: 18mm 16mm 17mm; }
    * { box-sizing: border-box; }
    body { margin: 0; color: #111; font-family: Arial, Helvetica, sans-serif; font-size: 10.5pt; line-height: 1.35; }
    h1 { margin: 0 0 12pt; font-size: 15pt; line-height: 1.25; }
    h2 { margin: 15pt 0 7pt; font-size: 12pt; }
    p { margin: 0 0 8pt; }
    ul { margin: 4pt 0 9pt 20pt; padding: 0; }
    li { margin-bottom: 3pt; }
    .intro { page-break-after: always; }
    .form-title { margin-bottom: 4pt; font-size: 15pt; font-weight: 700; }
    .instruction { margin-bottom: 13pt; }
    .field { display: grid; grid-template-columns: 52mm 1fr; gap: 4mm; margin: 7pt 0; }
    .value { min-height: 17pt; border-bottom: 0.7pt solid #333; font-weight: 600; }
    .section-title { margin: 15pt 0 7pt; padding-bottom: 3pt; border-bottom: 1.2pt solid #111; font-size: 11.5pt; font-weight: 700; }
    .practitioner-block { break-before: page; page-break-before: always; }
    table { width: 100%; margin: 8pt 0 13pt; border-collapse: collapse; page-break-inside: avoid; }
    th, td { padding: 7pt; border: 0.8pt solid #555; vertical-align: top; }
    th { text-align: left; font-weight: 700; background: #eee; }
    th:last-child, td.answer { width: 43mm; }
    .box { display: inline-block; width: 11pt; height: 11pt; margin: 0 3pt 0 10pt; border: 0.8pt solid #111; vertical-align: -1.5pt; text-align: center; line-height: 9pt; }
    .box:first-child { margin-left: 0; }
    .box.checked::after { content: "X"; font-size: 9pt; font-weight: 700; }
    .small { font-size: 9pt; color: #333; }
    @media screen { body { width: 210mm; min-height: 297mm; margin: 0 auto; padding: 18mm 16mm 17mm; background: white; } }
  </style></head><body>
    <section class="intro">
      <h1>Special arrangements for the signing of the applicant declaration on application forms during the COVID-19 pandemic</h1>
      <p>The COVID-19 pandemic is changing the way our organisation and private legal practitioners provide legal services to Victorians in need. To comply with social distancing measures practitioners will increasingly provide services by telephone and email. This creates difficulties for legal practitioners obtaining a person's signature on an application form.</p>
      <p>Our organisation has determined that the requirement to adhere to social distancing in light of the COVID-19 pandemic amounts to a special circumstance under s. 23(4) of the Legal Aid Act 1978. As such, we have determined that applicants who apply for legal assistance with the help of a legal practitioner can make the required declarations by phone or email instead of signing application forms in person. The legal practitioner completing the application will maintain a record of the declaration in lieu of a signature.</p>
      <p>We have created pro forma documents for practitioners to use that will satisfy us that the applicant has understood and agreed to the required declarations. Practitioners can choose which method is appropriate in the circumstances of their client/applicant. The legal practitioner must retain a copy of the telephone or email declaration record on file along with the completed application form.</p>
    </section>

    <section>
      <div class="form-title">Application for legal assistance - telephone declaration record</div>
      <p class="instruction">This document is to be used by practitioners when a client makes a declaration about their application by telephone.</p>
      <div class="field"><strong>Client name:</strong><div class="value">${escapeHtml(clientName)}</div></div>
      <div class="field"><strong>File reference:</strong><div class="value">${escapeHtml(fileReference)}</div></div>
      <div class="field"><strong>Date and time of client meeting:</strong><div class="value">${escapeHtml(meetingDate)}</div></div>
      <p><strong>Following the completion of an application for legal assistance by telephone:</strong></p>
      <ul><li>read the applicant declaration to the client</li><li>give the client the opportunity to ask questions</li><li>ask the client to make the declaration in lieu of a signature.</li></ul>

      <div class="section-title">Applicant declaration (to be read to client)</div>
      <p>In relation to my application for legal assistance:</p>
      <p>I, <strong>${escapeHtml(declarationName)}</strong>${data.declaration_of ? `, of <strong>${escapeHtml(data.declaration_of)}</strong>` : ""}:</p>
      <p>acknowledge that it is an offence to:</p>
      <ul><li>fail to disclose all information relevant to this application for legal assistance</li><li>provide false information to Victoria Legal Aid</li><li>make a false statement about this application for legal assistance</li></ul>
      <p>understand that I can ask for a copy of Victoria Legal Aid's privacy statement to be sent to me</p>
      <p>consent to the submission of the application for legal assistance by electronic means to Victoria Legal Aid via the ATLAS grants management system.</p>

      <div class="section-title">Centrelink consent and authority</div>
      <p>I, <strong>${escapeHtml(centrelinkName)}</strong>, authorise:</p>
      <ul><li>Victoria Legal Aid to check with Centrelink about my customer details and concession card status</li><li>Services Australia to provide the results of that enquiry to Victoria Legal Aid.</li></ul>
      <p>I understand that:</p>
      <ul><li>Services Australia will disclose my personal information to Victoria Legal Aid to confirm my eligibility for legal assistance</li><li>this consent remains valid while I am a client of Victoria Legal Aid unless I withdraw it</li><li>I can provide proof of my circumstances directly to Victoria Legal Aid</li><li>if I withdraw consent or do not provide proof, I may not be eligible for a grant of aid.</li></ul>

      <div class="practitioner-block">
      <div class="section-title">Legal practitioner declaration</div>
      <table><thead><tr><th>Declaration</th><th>Response</th></tr></thead><tbody>
        ${answerRow("Did the client demonstrate an understanding that it is an offence to lie or fail to disclose relevant information in the aid application?", data.practitioner_q1)}
        ${answerRow("Is the client aware that they can ask for a copy of VLA's privacy statement to be sent to them?", data.practitioner_q2)}
        ${answerRow("Does the client consent to electronic submission of the legal aid application form?", data.practitioner_q3)}
        ${answerRow("Does the client consent to VLA checking the information provided with Centrelink?", data.practitioner_q4)}
        <tr><td>Client consent obtained via:</td><td class="answer"><strong>${escapeHtml(consentMethod)}</strong></td></tr>
      </tbody></table>
      </div>
      <p>I declare:</p>
      <ul><li>I have given the client the opportunity to ask questions about the declaration</li><li>I have taken reasonable steps to ensure that the client understood the declaration</li><li>I am satisfied that the client's response indicated acceptance of the declaration</li></ul>
      <div class="field"><strong>Completed by:</strong><div class="value">${escapeHtml(lawyerName)}</div></div>
      <div class="field"><strong>Date:</strong><div class="value">${escapeHtml(lawyerDate)}</div></div>
      <p class="small">Prepared for ${escapeHtml(firmName)} from the completed legal aid application.</p>
    </section>
  </body></html>`;
}

export function printTelephoneDeclaration(data, brandType = "lacw", openWindow = window.open) {
  const printWindow = openWindow("", "_blank", "width=900,height=1000");
  if (!printWindow) throw new Error("Allow pop-ups to print the telephone declaration.");
  printWindow.document.open();
  printWindow.document.write(buildTelephoneDeclarationHtml(data, brandType));
  printWindow.document.close();
  printWindow.focus();
  printWindow.setTimeout(() => printWindow.print(), 250);
}

// The same declaration as a PDF file, for attaching to an emailed application. It is drawn
// from the printed document page by page - the introduction, the declarations, and the
// practitioner's declaration - so the attachment looks like the printed record.
export async function createTelephoneDeclarationPdf(data, brandType = "lacw") {
  const [{ default: html2canvas }, { jsPDF }] = await Promise.all([import("html2canvas"), import("jspdf")]);

  const frame = document.createElement("iframe");
  frame.setAttribute("aria-hidden", "true");
  frame.style.cssText = "position:fixed;left:-10000px;top:0;width:820px;height:1200px;border:0;visibility:hidden;";
  document.body.appendChild(frame);
  try {
    const doc = frame.contentDocument;
    doc.open();
    doc.write(buildTelephoneDeclarationHtml(data, brandType));
    doc.close();

    const intro = doc.querySelector("section.intro");
    const record = intro.nextElementSibling;
    const practitioner = record.querySelector(".practitioner-block");
    // Everything from the practitioner's declaration onwards (its questions, "I declare",
    // and the lawyer's details) is the last page; everything before it is the middle page.
    const parts = [...record.children];
    const split = parts.indexOf(practitioner);
    const declarations = parts.slice(0, split);
    const practitionerPage = parts.slice(split);
    const show = (els, visible) => els.forEach((el) => { el.style.display = visible ? "" : "none"; });
    practitioner.style.breakBefore = "auto";

    const pages = [
      () => { show([intro], true); show([record], false); },
      () => { show([intro], false); show([record], true); show(declarations, true); show(practitionerPage, false); },
      () => { show(declarations, false); show(practitionerPage, true); },
    ];

    const pdf = new jsPDF({ unit: "mm", format: "a4" });
    for (let i = 0; i < pages.length; i += 1) {
      pages[i]();
      const canvas = await html2canvas(doc.body, { scale: 2, backgroundColor: "#ffffff", logging: false, windowWidth: 820 });
      // Fit the page to A4, shrinking only if a long answer makes it taller than a page.
      const width = 210;
      const height = (canvas.height * width) / canvas.width;
      const scale = Math.min(1, 297 / height);
      if (i > 0) pdf.addPage();
      pdf.addImage(canvas.toDataURL("image/jpeg", 0.92), "JPEG", (210 - width * scale) / 2, 0, width * scale, height * scale);
    }
    return new Uint8Array(pdf.output("arraybuffer"));
  } finally {
    frame.remove();
  }
}
